import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, SendIcon, SquareIcon, PaperclipIcon, CpuIcon, FlaskConicalIcon, ZapIcon } from 'lucide-react';
import { ChatRuntime } from '../../types';
import { useSessionStore } from '../../stores/sessionStore';
import { useModelStore } from '../../stores/modelStore';
import { Badge, Button } from '../common/ui';
import { MessageBubble } from './MessageItems';
interface Props {
  runtime: ChatRuntime;
  open: boolean;
  onClose: () => void;
  isTest?: boolean;
  isTemporary?: boolean;
  /** When provided, the dialog binds to this existing session instead of creating a new one. */
  existingSessionId?: string;
  /** Factory to create a fresh session when none is provided. Returns the new sessionId. */
  createSession?: () => string;
}
const QUICK_PROMPTS = ['检查服务器状态', '分析最近的错误日志', '总结要点'];
export function ChatDialog({
  runtime,
  open,
  onClose,
  isTest,
  isTemporary,
  existingSessionId,
  createSession
}: Props) {
  const models = useModelStore(s => s.models);
  const sendMessage = useSessionStore(s => s.sendMessage);
  const interrupt = useSessionStore(s => s.interrupt);
  const sessions = useSessionStore(s => s.sessions);
  const messagesMap = useSessionStore(s => s.messages);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const model = models.find(m => m.modelId === runtime.defaultModelId);
  const session = sessions.find(s => s.sessionId === sessionId);
  const messages = sessionId ? messagesMap[sessionId] || [] : [];
  const running = session?.status === 'running';
  useEffect(() => {
    if (open && !sessionId) {
      if (existingSessionId) {
        setSessionId(existingSessionId);
      } else if (createSession) {
        setSessionId(createSession());
      }
    }
    if (!open) setSessionId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [messages]);
  const handleSend = () => {
    if (!input.trim() || !sessionId || running) return;
    sendMessage(sessionId, runtime, input.trim());
    setInput('');
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const hasBindings = runtime.skills.length > 0 || runtime.knowledgeBases.length > 0;
  return createPortal(<AnimatePresence data-id="element-37">
      {open && <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-id="element-38">
          <motion.div className="absolute inset-0 bg-black/60 backdrop-blur-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} data-id="element-39" />
          <motion.div role="dialog" aria-modal="true" aria-label={`${runtime.name} 对话框`} className="relative z-10 flex h-full w-full max-w-xl flex-col border-l border-slate-800 bg-slate-900 shadow-2xl" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 36
      }} data-id="element-40">
            {/* Header */}
            <div className="border-b border-slate-800 px-4 py-3" data-id="element-41">
              <div className="flex items-center gap-2" data-id="element-42">
                <h2 className="text-base font-semibold text-slate-100" data-id="element-43">
                  {runtime.name}
                </h2>
                {isTemporary && <Badge tone="blue" data-id="element-44">
                    <ZapIcon className="h-3 w-3" data-id="element-45" /> 临时会话
                  </Badge>}
                {isTest && <Badge tone="amber" data-id="element-46">
                    <FlaskConicalIcon className="h-3 w-3" data-id="element-47" /> 测试模式
                  </Badge>}
                <button onClick={onClose} aria-label="关闭" className="ml-auto rounded-lg p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200" data-id="element-48">
                  <XIcon className="h-5 w-5" data-id="element-49" />
                </button>
              </div>
              {runtime.description && <p className="mt-1 text-xs text-slate-400" data-id="element-50">
                  {runtime.description}
                </p>}
              <div className="mt-2 flex flex-wrap items-center gap-1.5" data-id="element-51">
                <Badge tone="indigo" data-id="element-52">
                  <CpuIcon className="h-3 w-3" data-id="element-53" />
                  {model?.name || '未指定模型'}
                </Badge>
                {runtime.skills.map(sk => <Badge key={sk.skillId} tone="purple" data-id="element-54">
                    {sk.name}
                  </Badge>)}
                {runtime.knowledgeBases.map(kb => <Badge key={kb.knowledgeBaseId} tone="green" data-id="element-55">
                    {kb.name}
                  </Badge>)}
                {!hasBindings && <span className="text-xs text-slate-500" data-id="element-56">
                    未绑定 Skills / 知识库
                  </span>}
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4" data-id="element-57">
              {messages.length === 0 && <div className="flex h-full flex-col items-center justify-center text-center text-slate-500" data-id="element-58">
                  <p className="text-sm" data-id="element-59">
                    输入指令开始
                    {isTemporary ? '临时对话' : `与 ${runtime.name} 对话`}
                  </p>
                  <div className="mt-3 flex flex-wrap justify-center gap-2" data-id="element-60">
                    {QUICK_PROMPTS.map(q => <button key={q} onClick={() => setInput(q)} className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-800" data-id="element-61">
                        {q}
                      </button>)}
                  </div>
                </div>}
              {messages.map(m => <MessageBubble key={m.id} message={m} data-id="element-62" />)}
            </div>

            {/* Runtime status bar */}
            {session && <div className="flex items-center gap-2 border-t border-slate-800 px-4 py-1.5 text-xs" data-id="element-63">
                <span className={`h-2 w-2 rounded-full ${running ? 'animate-pulse bg-emerald-400' : session.status === 'error' ? 'bg-red-400' : 'bg-slate-500'}`} data-id="element-64" />
                <span className="text-slate-400" data-id="element-65">
                  状态：
                  {running ? '运行中' : session.status === 'interrupted' ? '已中断' : session.status === 'error' ? '错误' : '空闲'}
                </span>
              </div>}

            {/* Input bar */}
            <div className="border-t border-slate-800 p-3" data-id="element-66">
              <div className="flex items-end gap-2 rounded-xl border border-slate-700 bg-slate-800/60 p-2" data-id="element-67">
                <button className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-700" aria-label="上传文件" data-id="element-68">
                  <PaperclipIcon className="h-4 w-4" data-id="element-69" />
                </button>
                <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={onKeyDown} rows={1} placeholder="输入指令… (Enter 发送，Shift+Enter 换行)" className="max-h-32 flex-1 resize-none bg-transparent py-1 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none" data-id="element-70" />
                {running ? <Button variant="danger" size="sm" onClick={() => sessionId && interrupt(sessionId)} data-id="element-71">
                    <SquareIcon className="h-3.5 w-3.5" data-id="element-72" /> 中断
                  </Button> : <Button size="sm" onClick={handleSend} disabled={!input.trim()} data-id="element-73">
                    <SendIcon className="h-3.5 w-3.5" data-id="element-74" /> 发送
                  </Button>}
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>, document.body);
}